import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavBarState } from '../../redux/navbar';
import { useDebouncedCallback } from '../../utils/hooks/useDebouncedCallback';
import { NAV_HOVER_CLICK_GRACE_MS, NAV_HOVER_OPEN_DELAY_MS, NavMenuType } from './navbar-constants';

type NavMenuControl = [boolean, (show: boolean) => void];

export const useCloseNav = () => {
  const { setOpenTopMenu, setOpenSubMenu, setOpenTertiaryMenu } = useNavBarState();

  return useCallback(() => {
    setOpenTertiaryMenu(undefined);
    setOpenSubMenu(undefined);
    setOpenTopMenu(undefined);
  }, [setOpenTopMenu, setOpenSubMenu, setOpenTertiaryMenu]);
};

export const useNavClickOutside = (navRef: RefObject<HTMLElement | null>) => {
  const closeNav = useCloseNav();
  const closeNavRef = useRef(closeNav);
  useLayoutEffect(() => {
    closeNavRef.current = closeNav;
  });

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!navRef.current?.contains(target) && !target.closest('.nav-popover')) {
        closeNavRef.current();
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeNavRef.current();
      }
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [navRef]);
};

export const useNavHoverOpen = (isOpen: boolean, open: () => void) => {
  const hoverOpenedAtRef = useRef<number | undefined>(undefined);

  const [start, cancel] = useDebouncedCallback(() => {
    if (isOpen) return;
    hoverOpenedAtRef.current = Date.now();
    open();
  }, NAV_HOVER_OPEN_DELAY_MS);

  const onMouseLeave = useCallback(() => {
    cancel();
    hoverOpenedAtRef.current = undefined;
  }, [cancel]);

  // True once, and only shortly after a hover-open: a click made on arrival should not close the menu
  // the hover just opened, but a later click is a deliberate close.
  const consumeHoverOpen = useCallback(() => {
    const hoverOpenedAt = hoverOpenedAtRef.current;
    hoverOpenedAtRef.current = undefined;
    return hoverOpenedAt !== undefined && Date.now() - hoverOpenedAt < NAV_HOVER_CLICK_GRACE_MS;
  }, []);

  // A click settles the menu state itself, so a still-pending hover must not reopen it after.
  const hoverHandlers = useMemo(
    () => ({ onMouseEnter: start, onMouseLeave, onClick: cancel }),
    [start, onMouseLeave, cancel]
  );

  return { hoverHandlers, consumeHoverOpen };
};

export const useNavTopMenu = (menuKey: NavMenuType): NavMenuControl => {
  const { openTopMenu, setOpenTopMenu, setOpenSubMenu, setOpenTertiaryMenu } = useNavBarState();

  return useMemo(
    () => [
      openTopMenu === menuKey,
      show => {
        if (!show) {
          setOpenTertiaryMenu(undefined);
          setOpenSubMenu(undefined);
          setOpenTopMenu(undefined);
          return;
        }

        if (show && menuKey !== openTopMenu) {
          setOpenTertiaryMenu(undefined);
          setOpenSubMenu(undefined);
          setOpenTopMenu(menuKey);
        }
      }
    ],
    [menuKey, openTopMenu, setOpenSubMenu, setOpenTertiaryMenu, setOpenTopMenu]
  );
};

export const useNavSubMenu = (subMenuKey: string): NavMenuControl => {
  const { openSubMenu, setOpenSubMenu, setOpenTertiaryMenu } = useNavBarState();

  return useMemo(
    () => [
      openSubMenu === subMenuKey,
      show => {
        if (!show) {
          setOpenTertiaryMenu(undefined);
          setOpenSubMenu(undefined);
          return;
        }

        if (show && subMenuKey !== openSubMenu) {
          setOpenTertiaryMenu(undefined);
          setOpenSubMenu(subMenuKey);
        }
      }
    ],
    [openSubMenu, setOpenSubMenu, setOpenTertiaryMenu, subMenuKey]
  );
};

export const useNavTertiaryMenu = (tertiaryMenuKey: string): NavMenuControl => {
  const { openTertiaryMenu, setOpenTertiaryMenu } = useNavBarState();

  return useMemo(
    () => [
      openTertiaryMenu === tertiaryMenuKey,
      show => {
        if (!show) {
          setOpenTertiaryMenu(undefined);
          return;
        }

        if (show && tertiaryMenuKey !== openTertiaryMenu) {
          setOpenTertiaryMenu(tertiaryMenuKey);
        }
      }
    ],
    [openTertiaryMenu, setOpenTertiaryMenu, tertiaryMenuKey]
  );
};
